import React, { useEffect, useState, useCallback, useMemo } from "react";
import MoshkyAnalyticsPanel from "@/components/MoshkyAnalyticsPanel";
import useApi from "@/hooks/useApi";
import ReportExportButton from "@/components/ReportExportButton";

// ── Types ─────────────────────────────────────────────────────────────────

interface AgentScore {
  agentId: number;
  agentName: string;
  avgScore: number | null;
  scored: number;
  positive: number;
  neutral: number;
  negative: number;
  recentCoachingNote: string | null;
}

interface ScoredConversation {
  id: number;
  startTime: string;
  agentName: string | null;
  qualityScore: number;
  qualitySentiment: string;
  qualityCoachingNote: string | null;
  topic: string | null;
}

interface PerformanceData {
  period: { start: string; end: string; days: number };
  avgScore: number | null;
  totalScored: number;
  sentimentBreakdown: { positive: number; neutral: number; negative: number };
  agents: AgentScore[];
  recentConversations: ScoredConversation[];
}

// ── Helpers ────────────────────────────────────────────────────────────────

function scoreColor(score: number | null) {
  if (score == null) return "var(--bs-secondary)";
  if (score >= 80) return "#22c55e";
  if (score >= 60) return "#f59e0b";
  return "#ef4444";
}

function scoreLabel(score: number | null) {
  if (score == null) return "—";
  if (score >= 90) return "Excellent";
  if (score >= 75) return "Good";
  if (score >= 60) return "Fair";
  return "Needs Improvement";
}

function SentimentBadge({ sentiment }: { sentiment: string }) {
  const colors: Record<string, string> = {
    positive: "#22c55e",
    neutral: "#94a3b8",
    negative: "#ef4444",
  };
  const icons: Record<string, string> = {
    positive: "😊",
    neutral: "😐",
    negative: "😞",
  };
  const color = colors[sentiment] ?? colors.neutral;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 500,
        background: color + "22",
        color,
        border: `1px solid ${color}44`,
      }}
    >
      {icons[sentiment] ?? "😐"} {sentiment ?? "neutral"}
    </span>
  );
}

function ScoreRing({ score }: { score: number | null }) {
  const color = scoreColor(score);
  const display = score != null ? `${score}` : "—";
  return (
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        border: `4px solid ${color}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <span style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1 }}>{display}</span>
      <span style={{ fontSize: 9, color: "var(--v-text-muted)", marginTop: 1 }}>/ 100</span>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────

export default function AgentPerformance() {
  const api = useApi();
  const [data, setData] = useState<PerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [expandedAgent, setExpandedAgent] = useState<number | null>(null);

  const exportStartDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d;
  }, [days]);
  const exportEndDate = useMemo(() => new Date(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.messaging.get(`api/admin/Reports/AgentPerformance?days=${days}`);
      if (!res.ok) throw new Error("Failed to load");
      const json = await res.json();
      setData(json);
    } catch {
      setError("Could not load agent performance data.");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 m-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
        {error}{" "}
        <button className="ml-2 text-xs font-medium text-slate-600 border border-slate-300 rounded px-2 py-1 hover:bg-slate-50 transition-colors" onClick={load}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const { avgScore, totalScored, sentimentBreakdown, agents, recentConversations, period } = data;

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Agent Performance</h2>
          <p style={{ margin: "4px 0 0", color: "var(--v-text-muted)", fontSize: 13 }}>
            AI-scored conversation quality · {period.start} – {period.end}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {[7, 14, 30, 90].map((d) => (
            <button
              key={d}
              className={`text-xs px-2.5 py-1 rounded border font-medium transition-colors ${days === d ? "bg-primary text-white border-primary" : "text-slate-600 border-slate-300 hover:bg-slate-50"}`}
              onClick={() => setDays(d)}
            >
              {d}d
            </button>
          ))}
          <MoshkyAnalyticsPanel reportName="Agent Performance" reportData={{ agents, avgScore, totalScored, sentimentBreakdown, period }} />
          <ReportExportButton
            defaultStartDate={exportStartDate}
            defaultEndDate={exportEndDate}
            reportType="agent-utilization"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 28 }}>
        <SummaryCard
          label="Avg Quality Score"
          value={avgScore != null ? `${avgScore}` : "—"}
          sub={avgScore != null ? scoreLabel(avgScore) : "No scored conversations"}
          color={scoreColor(avgScore)}
        />
        <SummaryCard label="Conversations Scored" value={`${totalScored}`} sub="AI-analyzed after resolve" color="#6366f1" />
        <SummaryCard
          label="Positive Sentiment"
          value={totalScored > 0 ? `${Math.round((sentimentBreakdown.positive / totalScored) * 100)}%` : "—"}
          sub={`${sentimentBreakdown.positive} of ${totalScored}`}
          color="#22c55e"
        />
        <SummaryCard
          label="Needs Attention"
          value={totalScored > 0 ? `${Math.round((sentimentBreakdown.negative / totalScored) * 100)}%` : "—"}
          sub={`${sentimentBreakdown.negative} negative sentiment`}
          color="#ef4444"
        />
      </div>

      {/* Per-Agent Scores */}
      {agents.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>By Agent</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {agents.map((agent) => (
              <div
                key={agent.agentId}
                style={{
                  background: "var(--v-bg-card)",
                  border: "1px solid var(--v-border)",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: "12px 16px",
                    cursor: "pointer",
                  }}
                  onClick={() => setExpandedAgent(expandedAgent === agent.agentId ? null : agent.agentId)}
                >
                  <ScoreRing score={agent.avgScore} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{agent.agentName}</div>
                    <div style={{ fontSize: 12, color: "var(--v-text-muted)", marginTop: 2 }}>
                      {agent.scored} conversations scored ·{" "}
                      <span style={{ color: "#22c55e" }}>{agent.positive} 😊</span>{" "}
                      <span style={{ color: "#94a3b8" }}>{agent.neutral} 😐</span>{" "}
                      <span style={{ color: "#ef4444" }}>{agent.negative} 😞</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: scoreColor(agent.avgScore) }}>
                      {scoreLabel(agent.avgScore)}
                    </span>
                    <i className={`fa fa-chevron-${expandedAgent === agent.agentId ? "up" : "down"}`} style={{ fontSize: 11, color: "var(--v-text-muted)" }} />
                  </div>
                </div>
                {expandedAgent === agent.agentId && agent.recentCoachingNote && (
                  <div
                    style={{
                      padding: "12px 16px",
                      background: "var(--v-bg-subtle)",
                      borderTop: "1px solid var(--v-border)",
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                    }}
                  >
                    <span style={{ fontSize: 18 }}>🤖</span>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--v-text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Latest AI Coaching Note
                      </div>
                      <p style={{ margin: 0, fontSize: 13, color: "var(--v-text-primary)", lineHeight: 1.5 }}>
                        {agent.recentCoachingNote}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Scored Conversations */}
      {recentConversations.length > 0 && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Recent Scored Conversations</h3>
          <div style={{ background: "var(--v-bg-card)", border: "1px solid var(--v-border)", borderRadius: 8, overflow: "hidden" }}>
            <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--v-bg-subtle)" }}>
                  <th>Conversation</th>
                  <th>Agent</th>
                  <th>Score</th>
                  <th>Sentiment</th>
                  <th>Coaching Note</th>
                </tr>
              </thead>
              <tbody>
                {recentConversations.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>#{c.id}</div>
                      <div style={{ fontSize: 11, color: "var(--v-text-muted)" }}>
                        {c.topic ?? "—"} · {new Date(c.startTime).toLocaleDateString()}
                      </div>
                    </td>
                    <td style={{ color: "var(--v-text-secondary)" }}>{c.agentName ?? "—"}</td>
                    <td>
                      <span style={{ fontWeight: 700, color: scoreColor(c.qualityScore), fontSize: 15 }}>
                        {c.qualityScore}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--v-text-muted)" }}> / 100</span>
                    </td>
                    <td><SentimentBadge sentiment={c.qualitySentiment} /></td>
                    <td style={{ maxWidth: 320 }}>
                      <span style={{ fontSize: 12, color: "var(--v-text-secondary)", lineHeight: 1.4, display: "block" }}>
                        {c.qualityCoachingNote ?? "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalScored === 0 && (
        <div style={{ textAlign: "center", padding: "60px 24px", color: "var(--v-text-muted)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No scored conversations yet</div>
          <p style={{ fontSize: 13 }}>
            Conversation quality scoring runs automatically after conversations resolve.
            Enable it in <strong>Settings → AI Features → Conversation Quality</strong>.
          </p>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  return (
    <div
      style={{
        background: "var(--v-bg-card)",
        border: "1px solid var(--v-border)",
        borderRadius: 10,
        padding: "16px 20px",
        borderLeft: `4px solid ${color}`,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--v-text-muted)", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--v-text-muted)", marginTop: 4 }}>{sub}</div>
    </div>
  );
}
